import { token } from '@atlaskit/tokens';
import { readableTextColor } from '@atlassian/color-picker';

import { HUB_PRIMARY_COLOR_CSS_VAR, HUB_PRIMARY_TEXT_COLOR_CSS_VAR } from '../../constants';
import { ELEMENT_DEFAULTS, HUB_COLOR_CSS_VARS } from '../../utils/hub-brand-palette';
import type { HubBrandPalette, PaletteRef } from '../../utils/hub-brand-palette';

export type HubDefaultParameters = {
	backgroundColor: string;
	/**
	 * Optional v2-only icon-color override (currently emitted for the JSM Help
	 * extension). Resolves to `var(--hub-color-primary-800, #hex)` via the
	 * `<HubThemingProvider>` wrapper's inline style. Absent on the legacy path
	 * and for elements that don't render an icon.
	 */
	iconColor?: string;
	/**
	 * `true` when this object was computed against an advanced-theming palette
	 * (gate ON in help-center). `false` for the legacy `var(--hub-primary-*)`
	 * shape returned to Confluence / CSM / gate-OFF help-center.
	 *
	 * Lets per-extension manifests opt into v2-only seed overrides (e.g. a
	 * spec-compliant neutral border for links-menu) without misfiring on the
	 * legacy shape — `backgroundColor` is always populated, so its presence
	 * alone is not a safe gate signal.
	 */
	isAdvancedThemingEnabled: boolean;
	textColor: string;
};

/**
 * Day-0 default shade that bespoke extensions inherit through
 * `CommonHubExtensionsDependenciesProvider` when an extension hasn't pinned a
 * background of its own. Kept local to this resolver — it is intentionally
 * decoupled from `ELEMENT_DEFAULTS` because the per-element map is the
 * source-of-truth for *named* element slots (e.g. `callout.bg`), whereas this
 * is the resolver's own generic fallback shared across every extension.
 */
const DEFAULT_HUB_BACKGROUND_SHADE: PaletteRef = 'primary.200';

/**
 * Per-element bg shade overrides. When an extension wants a different default
 * shade than the resolver's generic `primary.200`, it's mapped here. Keys
 * match the `ELEMENT_DEFAULTS` map; values are the slot suffix to look up.
 */
const ELEMENT_BG_SHADE: { readonly [key: string]: PaletteRef } = {
	section: ELEMENT_DEFAULTS['section.bg'],
};

/**
 * Per-element icon-color shade overrides. Currently only JSM Help renders an
 * icon; values are looked up in `ELEMENT_DEFAULTS`.
 */
const ELEMENT_ICON_SHADE: { readonly [key: string]: PaletteRef } = {
	'jsm-help': ELEMENT_DEFAULTS['jsm-help.icon'],
};

export type HubDefaultParametersOptions = {
	/**
	 * Optional element key. When supplied with a `palette`, the resolver
	 * picks the element-specific default shade from `ELEMENT_DEFAULTS`
	 * instead of the generic `primary.200`. Used by the section extension
	 * to seed `accent.100` instead of `primary.200`, and by JSM Help to
	 * additionally seed `iconColor: primary.800`.
	 */
	element?: 'section' | 'jsm-help';
	/**
	 * Premium-locked tenants seed every element with the raw `primaryColor`
	 * instead of a palette tint (bypassing the per-element shade overrides).
	 */
	isBrandPaletteLocked?: boolean;
	palette?: HubBrandPalette;
	/** Brand colour used when `isBrandPaletteLocked`; falls back to `primary.600`. */
	primaryColor?: string;
};

export const getHubDefaultParameters = (
	options: HubDefaultParametersOptions = {},
): HubDefaultParameters => {
	if (options.palette) {
		if (options.isBrandPaletteLocked) {
			// One brand colour across every element — no palette tints. The
			// `--hub-primary-color` var tracks brand edits; the hex is the fallback.
			const primary = options.primaryColor ?? options.palette['primary.600'];
			const iconShade = options.element && ELEMENT_ICON_SHADE[options.element];
			return {
				backgroundColor: `var(${HUB_PRIMARY_COLOR_CSS_VAR}, ${primary})`,
				textColor: readableTextColor(primary, '@atlassian/company-hub-common'),
				isAdvancedThemingEnabled: true,
				...(iconShade ? { iconColor: `var(${HUB_PRIMARY_COLOR_CSS_VAR}, ${primary})` } : {}),
			};
		}

		const bgShade: PaletteRef =
			(options.element && ELEMENT_BG_SHADE[options.element]) || DEFAULT_HUB_BACKGROUND_SHADE;
		const iconShade: PaletteRef | undefined =
			options.element && ELEMENT_ICON_SHADE[options.element];
		// Gate ON:
		//   - bg = CSS-var ref so the cascade tracks brand-color edits via the
		//     <HubThemingProvider> wrapper's inline `style`.
		//   - text = WCAG-readable black/white computed against the resolved
		//     bg hex. Stored as a literal so it remains correct even when
		//     admin overrides bg per-element (the auto-transition hook in
		//     the panel handles per-element flips at edit time).
		//   - icon = CSS-var ref (only when element opts in via ELEMENT_ICON_SHADE).
		return {
			backgroundColor: `var(${HUB_COLOR_CSS_VARS[bgShade]})`,
			textColor: readableTextColor(options.palette[bgShade], '@atlassian/company-hub-common'),
			isAdvancedThemingEnabled: true,
			...(iconShade
				? { iconColor: `var(${HUB_COLOR_CSS_VARS[iconShade]}, ${options.palette[iconShade]})` }
				: {}),
		};
	}

	return {
		backgroundColor: `var(${HUB_PRIMARY_COLOR_CSS_VAR}, ${token('color.background.accent.gray.subtler')})`,
		textColor: `var(${HUB_PRIMARY_TEXT_COLOR_CSS_VAR}, ${token('color.text')})`,
		isAdvancedThemingEnabled: false,
	};
};
